import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Form, Modal, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  deleteEmployee,
  deleteSalary,
  getPayrollPreview,
  listEmployees,
  listLinkableMembers,
  listSalaryHistory,
  runPayroll,
  saveEmployee,
  setSalary,
} from "../../../services/payrollApi";

const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const errorMessage = (error, fallback) => error?.response?.data?.message || fallback;

const dayLabel = (iso) => {
  if (!iso) return "-";
  const [year, month, day] = String(iso).split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });
};

const monthLabel = (key) => {
  if (!key) return "";
  const [year, month] = String(key).split("-").map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
};

const currentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const shiftMonth = (key, by) => {
  const [year, month] = String(key).split("-").map(Number);
  const moved = new Date(year, month - 1 + by, 1);
  return `${moved.getFullYear()}-${String(moved.getMonth() + 1).padStart(2, "0")}`;
};

const firstOfMonth = (key) => `${key}-01`;

const blankEmployee = () => ({
  leadTeamMemberId: "",
  name: "",
  designation: "",
  joinedOn: "",
  exitedOn: "",
  active: true,
  notes: "",
  monthlySalary: "",
  salaryEffectiveFrom: "",
});

// What each preview row means, kept next to the colour so the two never drift apart.
const STATUS = {
  TO_CREATE: { bg: "primary", text: "Will be added" },
  TO_UPDATE: { bg: "warning", text: "Will be corrected" },
  POSTED: { bg: "success", text: "In ledger" },
  NO_SALARY: { bg: "secondary", text: "No salary set" },
  EXTRA: { bg: "danger", text: "Unexpected entry" },
};

export default function PayrollManager({ onLedgerChanged }) {
  const [month, setMonth] = useState(currentMonth);
  const [employees, setEmployees] = useState([]);
  const [members, setMembers] = useState([]);
  const [preview, setPreview] = useState(null);
  const [editing, setEditing] = useState(null);
  const [salaryFor, setSalaryFor] = useState(null);
  const [history, setHistory] = useState([]);
  const [newRate, setNewRate] = useState({ monthlyAmount: "", effectiveFrom: "", notes: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rows, memberRows, previewRows] = await Promise.all([
        listEmployees(false),
        listLinkableMembers(),
        getPayrollPreview(month),
      ]);
      setEmployees(rows);
      setMembers(memberRows);
      setPreview(previewRows);
    } catch (error) {
      toast.error(errorMessage(error, "Could not load payroll"));
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const patch = (field, value) => setEditing((prev) => ({ ...prev, [field]: value }));

  const monthlyRun = useMemo(
    () => employees.reduce((sum, row) => sum + (row.active ? Number(row.currentSalary || 0) : 0), 0),
    [employees]);

  const openNew = () => setEditing(blankEmployee());

  const openEdit = (row) => setEditing({
    ...blankEmployee(),
    ...row,
    leadTeamMemberId: row.leadTeamMemberId ?? "",
    designation: row.designation || "",
    joinedOn: row.joinedOn || "",
    exitedOn: row.exitedOn || "",
    notes: row.notes || "",
  });

  /** Picking a lead team member fills the name in, so the two stay recognisably the same person. */
  const pickMember = (value) => {
    const memberId = value ? Number(value) : "";
    const member = members.find((row) => row.id === memberId);
    setEditing((prev) => ({
      ...prev,
      leadTeamMemberId: memberId,
      name: prev.name?.trim() ? prev.name : member?.name || "",
    }));
  };

  const saveRow = async () => {
    if (!editing.name.trim()) {
      toast.error("Enter the employee name");
      return;
    }
    if (!editing.id && editing.monthlySalary && Number(editing.monthlySalary) <= 0) {
      toast.error("Monthly salary must be greater than zero");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        id: editing.id,
        leadTeamMemberId: editing.leadTeamMemberId === "" ? null : Number(editing.leadTeamMemberId),
        name: editing.name.trim(),
        designation: editing.designation.trim() || null,
        joinedOn: editing.joinedOn || null,
        exitedOn: editing.exitedOn || null,
        active: editing.active !== false,
        notes: editing.notes.trim() || null,
      };
      if (!editing.id && editing.monthlySalary) {
        payload.monthlySalary = Number(editing.monthlySalary);
        payload.salaryEffectiveFrom = editing.salaryEffectiveFrom || null;
      }
      await saveEmployee(payload);
      toast.success(editing.id ? "Employee updated" : "Employee added");
      setEditing(null);
      await load();
    } catch (error) {
      toast.error(errorMessage(error, "Could not save the employee"));
    } finally {
      setSaving(false);
    }
  };

  const removeRow = async (row) => {
    if (!window.confirm(`Remove ${row.name} from the payroll list?`)) return;
    try {
      await deleteEmployee(row.id);
      toast.success("Employee removed");
      await load();
    } catch (error) {
      toast.error(errorMessage(error, "Could not remove the employee"));
    }
  };

  const openSalary = async (row) => {
    setSalaryFor(row);
    setNewRate({ monthlyAmount: "", effectiveFrom: firstOfMonth(month), notes: "" });
    try {
      setHistory(await listSalaryHistory(row.id));
    } catch (error) {
      toast.error(errorMessage(error, "Could not load the salary history"));
    }
  };

  const saveRate = async () => {
    if (!newRate.monthlyAmount || Number(newRate.monthlyAmount) <= 0) {
      toast.error("Enter a salary greater than zero");
      return;
    }
    setSaving(true);
    try {
      await setSalary(salaryFor.id, {
        monthlyAmount: Number(newRate.monthlyAmount),
        effectiveFrom: newRate.effectiveFrom || null,
        notes: newRate.notes.trim() || null,
      });
      toast.success("Salary recorded");
      setHistory(await listSalaryHistory(salaryFor.id));
      setNewRate({ monthlyAmount: "", effectiveFrom: firstOfMonth(month), notes: "" });
      await load();
    } catch (error) {
      toast.error(errorMessage(error, "Could not record the salary"));
    } finally {
      setSaving(false);
    }
  };

  const removeRate = async (row) => {
    if (!window.confirm("Remove this salary entry? The one before it becomes current again.")) return;
    try {
      await deleteSalary(row.id);
      setHistory(await listSalaryHistory(salaryFor.id));
      await load();
    } catch (error) {
      toast.error(errorMessage(error, "Could not remove the salary entry"));
    }
  };

  const run = async () => {
    const pending = (preview?.toCreate || 0) + (preview?.toUpdate || 0)
      + (preview?.incentiveToCreate || 0) + (preview?.incentiveToUpdate || 0);
    if (!pending) {
      toast.info("Nothing to post — the ledger already matches this month.");
      return;
    }
    const combined = Number(preview.total || 0) + Number(preview.incentiveTotal || 0);
    if (!window.confirm(
      `Post ${money(combined)} of salary and incentive for ${monthLabel(month)} `
      + "to the expense ledger?")) {
      return;
    }
    setRunning(true);
    try {
      const result = await runPayroll(month);
      const added = result.created + result.incentivesCreated;
      const corrected = result.updated + result.incentivesUpdated;
      const same = result.unchanged + result.incentivesUnchanged;
      toast.success(`${added} added, ${corrected} corrected, ${same} unchanged`);
      setPreview(result.preview);
      if (added || corrected) {
        onLedgerChanged?.();
      }
    } catch (error) {
      toast.error(errorMessage(error, "Could not run payroll"));
    } finally {
      setRunning(false);
    }
  };

  const pendingCount = (preview?.toCreate || 0) + (preview?.toUpdate || 0)
    + (preview?.incentiveToCreate || 0) + (preview?.incentiveToUpdate || 0);

  return (
    <Card className="shadow-sm mt-4">
      <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div>
          <div className="fw-semibold">Salaries &amp; Payroll</div>
          <div className="small text-muted">
            What each employee is paid a month, and what the team earned in incentive. Running a
            month posts a salary entry per employee and an incentive entry per member into the
            expense ledger above, so every total already counts both.
          </div>
        </div>
        <Button size="sm" onClick={openNew}>+ Employee</Button>
      </Card.Header>
      <Card.Body>
        <div className="row g-2 align-items-end mb-3">
          <div className="col-lg-3 col-md-5">
            <Form.Label className="small">Payroll month</Form.Label>
            <Form.Control size="sm" type="month" value={month}
              onChange={(e) => setMonth(e.target.value || currentMonth())} />
          </div>
          <div className="col-lg-4 col-md-7 d-flex flex-wrap gap-2">
            <Button size="sm" variant="outline-secondary"
              onClick={() => setMonth(shiftMonth(month, -1))}>Previous</Button>
            <Button size="sm" variant="outline-secondary"
              onClick={() => setMonth(currentMonth())}>This Month</Button>
            <Button size="sm" variant="outline-secondary"
              onClick={() => setMonth(shiftMonth(month, 1))}>Next</Button>
          </div>
          <div className="col-lg-5 d-flex justify-content-lg-end">
            <Button size="sm" variant={pendingCount ? "primary" : "outline-primary"}
              disabled={running || loading} onClick={run}>
              {running ? <Spinner size="sm" className="me-2" /> : null}
              Run payroll for {monthLabel(month)}
            </Button>
          </div>
        </div>

        {preview && (
          <div className="row g-2 mb-3">
            <div className="col-xl col-md-4 col-6">
              <div className="border rounded p-2 h-100">
                <div className="small text-muted">{monthLabel(month)} salary</div>
                <div className="fs-5 fw-semibold">{money(preview.total)}</div>
                <div className="small text-muted">{preview.lines?.length || 0} people</div>
              </div>
            </div>
            <div className="col-xl col-md-4 col-6">
              <div className="border rounded p-2 h-100">
                <div className="small text-muted">{monthLabel(month)} incentive</div>
                <div className="fs-5 fw-semibold">{money(preview.incentiveTotal)}</div>
                <div className="small text-muted">{preview.incentiveLines?.length || 0} members</div>
              </div>
            </div>
            <div className="col-xl col-md-4 col-6">
              <div className="border rounded p-2 h-100">
                <div className="small text-muted">Already in the ledger</div>
                <div className="fs-5 fw-semibold">
                  {money(Number(preview.postedTotal || 0) + Number(preview.incentivePostedTotal || 0))}
                </div>
                <div className="small text-muted">Counted in expenses above</div>
              </div>
            </div>
            <div className="col-xl col-md-4 col-6">
              <div className="border rounded p-2 h-100">
                <div className="small text-muted">Still to post</div>
                <div className="fs-5 fw-semibold">{pendingCount}</div>
                <div className="small text-muted">
                  {preview.toCreate + preview.incentiveToCreate} to add ·{" "}
                  {preview.toUpdate + preview.incentiveToUpdate} to correct
                </div>
              </div>
            </div>
            <div className="col-xl col-md-4 col-6">
              <div className="border rounded p-2 h-100">
                <div className="small text-muted">Salary run rate</div>
                <div className="fs-5 fw-semibold">{money(monthlyRun)}</div>
                <div className="small text-muted">Active employees at today's salary</div>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-muted"><Spinner size="sm" className="me-2" />Loading payroll...</div>
        ) : (
          <>
            <div className="fw-semibold mb-2">Salary — {monthLabel(month)}</div>
            <div className="table-responsive mb-4">
              <Table bordered hover size="sm" className="align-middle mb-0">
                <thead>
                  <tr>
                    <th style={{ width: 44 }}>#</th>
                    <th>Employee</th>
                    <th>Monthly salary</th>
                    <th>Days paid</th>
                    <th className="text-end">This month</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {preview?.lines?.length ? preview.lines.map((line, index) => {
                    const status = STATUS[line.status] || { bg: "secondary", text: line.status };
                    return (
                      <tr key={line.employeeId}>
                        <td className="small fw-semibold">{index + 1}</td>
                        <td>
                          {line.name}
                          {line.designation && <div className="small text-muted">{line.designation}</div>}
                        </td>
                        <td>{line.monthlyAmount ? money(line.monthlyAmount) : <span className="text-muted">-</span>}</td>
                        <td className="small">{line.paidDays} / {line.daysInMonth}</td>
                        <td className="text-end fw-semibold">{money(line.amount)}</td>
                        <td>
                          <Badge bg={status.bg}>{status.text}</Badge>
                          {line.note && <div className="small text-muted">{line.note}</div>}
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-3">
                        No one on payroll for this month yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            <div className="fw-semibold mb-2">Incentive — {monthLabel(month)}</div>
            <div className="table-responsive mb-4">
              <Table bordered hover size="sm" className="align-middle mb-0">
                <thead>
                  <tr>
                    <th style={{ width: 44 }}>#</th>
                    <th>Member</th>
                    <th className="text-end">Earned</th>
                    <th className="text-end">Adjustments</th>
                    <th className="text-end">This month</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {preview?.incentiveLines?.length ? preview.incentiveLines.map((line, index) => {
                    const status = STATUS[line.status] || { bg: "secondary", text: line.status };
                    return (
                      <tr key={line.memberId}>
                        <td className="small fw-semibold">{index + 1}</td>
                        <td>
                          {line.name}
                          {line.team && <div className="small text-muted">{line.team}</div>}
                        </td>
                        <td className="text-end">{money(line.earned)}</td>
                        <td className="text-end">
                          {Number(line.adjustments || 0) === 0
                            ? <span className="text-muted">—</span>
                            : money(line.adjustments)}
                        </td>
                        <td className="text-end fw-semibold">{money(line.amount)}</td>
                        <td>
                          <Badge bg={status.bg}>{status.text}</Badge>
                          {line.note && <div className="small text-muted">{line.note}</div>}
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-3">
                        No incentive earned this month.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            <div className="fw-semibold mb-2">Employees ({employees.length})</div>
            <div className="table-responsive">
              <Table bordered hover size="sm" className="align-middle mb-0">
                <thead>
                  <tr>
                    <th style={{ width: 44 }}>#</th>
                    <th>Name</th>
                    <th>Portal login</th>
                    <th>Joined</th>
                    <th className="text-end">Current salary</th>
                    <th>Status</th>
                    <th style={{ width: 200 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {employees.length ? employees.map((row, index) => (
                    <tr key={row.id}>
                      <td className="small fw-semibold">{index + 1}</td>
                      <td>
                        {row.name}
                        {row.designation && <div className="small text-muted">{row.designation}</div>}
                      </td>
                      <td className="small">
                        {row.leadTeamMemberName || <span className="text-muted">Not linked</span>}
                      </td>
                      <td className="small">
                        {dayLabel(row.joinedOn)}
                        {row.exitedOn && (
                          <div className="text-muted">Left {dayLabel(row.exitedOn)}</div>
                        )}
                      </td>
                      <td className="text-end fw-semibold">
                        {row.currentSalary ? money(row.currentSalary) : <span className="text-muted">Not set</span>}
                        {row.currentSalaryFrom && (
                          <div className="small text-muted fw-normal">from {dayLabel(row.currentSalaryFrom)}</div>
                        )}
                      </td>
                      <td>
                        <Badge bg={row.active ? "success" : "secondary"}>
                          {row.active ? "Active" : "Inactive"}
                        </Badge>
                      </td>
                      <td className="d-flex gap-1">
                        <Button size="sm" variant="outline-primary" onClick={() => openSalary(row)}>Salary</Button>
                        <Button size="sm" variant="outline-secondary" onClick={() => openEdit(row)}>Edit</Button>
                        <Button size="sm" variant="outline-danger" onClick={() => removeRow(row)}>Delete</Button>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-3">
                        No employees yet. Add the people you pay a monthly salary.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </Card.Body>

      <Modal show={!!editing} onHide={() => setEditing(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">{editing?.id ? "Edit Employee" : "Add Employee"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editing && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label className="small">Portal login (optional)</Form.Label>
                <Form.Select size="sm" value={editing.leadTeamMemberId}
                  onChange={(e) => pickMember(e.target.value)}>
                  <option value="">Not linked — staff without a login</option>
                  {members.map((member) => (
                    <option key={member.id} value={member.id}
                      disabled={member.linked && member.id !== editing.leadTeamMemberId}>
                      {member.name}{member.team ? ` — ${member.team}` : ""}
                      {member.linked && member.id !== editing.leadTeamMemberId ? " (already added)" : ""}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  Links this salary to the person who logs into the lead portal.
                </Form.Text>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="small">Name</Form.Label>
                <Form.Control size="sm" value={editing.name}
                  onChange={(e) => patch("name", e.target.value)} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="small">Designation</Form.Label>
                <Form.Control size="sm" value={editing.designation}
                  onChange={(e) => patch("designation", e.target.value)} />
              </Form.Group>
              <div className="row g-2 mb-3">
                <div className="col-6">
                  <Form.Label className="small">Joined on</Form.Label>
                  <Form.Control size="sm" type="date" value={editing.joinedOn}
                    onChange={(e) => patch("joinedOn", e.target.value)} />
                </div>
                <div className="col-6">
                  <Form.Label className="small">Last working day</Form.Label>
                  <Form.Control size="sm" type="date" value={editing.exitedOn}
                    onChange={(e) => patch("exitedOn", e.target.value)} />
                </div>
              </div>
              {!editing.id && (
                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <Form.Label className="small">Monthly salary</Form.Label>
                    <Form.Control size="sm" type="number" min="0" value={editing.monthlySalary}
                      onChange={(e) => patch("monthlySalary", e.target.value)} />
                  </div>
                  <div className="col-6">
                    <Form.Label className="small">Salary effective from</Form.Label>
                    <Form.Control size="sm" type="date" value={editing.salaryEffectiveFrom}
                      onChange={(e) => patch("salaryEffectiveFrom", e.target.value)} />
                  </div>
                </div>
              )}
              <Form.Group className="mb-3">
                <Form.Label className="small">Notes</Form.Label>
                <Form.Control size="sm" as="textarea" rows={2} value={editing.notes}
                  onChange={(e) => patch("notes", e.target.value)} />
              </Form.Group>
              <Form.Check type="switch" id="employee-active" label="Active"
                checked={editing.active !== false}
                onChange={(e) => patch("active", e.target.checked)} />
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" variant="outline-secondary" onClick={() => setEditing(null)}>Cancel</Button>
          <Button size="sm" onClick={saveRow} disabled={saving}>
            {saving ? <Spinner size="sm" className="me-2" /> : null}Save
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!salaryFor} onHide={() => setSalaryFor(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">Salary — {salaryFor?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="small text-muted mb-3">
            A raise is recorded from a date rather than typed over the old figure, so months
            already paid keep costing what they cost.
          </div>
          <div className="row g-2 align-items-end mb-3">
            <div className="col-md-4">
              <Form.Label className="small">Monthly salary</Form.Label>
              <Form.Control size="sm" type="number" min="0" value={newRate.monthlyAmount}
                onChange={(e) => setNewRate((prev) => ({ ...prev, monthlyAmount: e.target.value }))} />
            </div>
            <div className="col-md-4">
              <Form.Label className="small">Effective from</Form.Label>
              <Form.Control size="sm" type="date" value={newRate.effectiveFrom}
                onChange={(e) => setNewRate((prev) => ({ ...prev, effectiveFrom: e.target.value }))} />
            </div>
            <div className="col-md-4">
              <Button size="sm" className="w-100" onClick={saveRate} disabled={saving}>
                {saving ? <Spinner size="sm" className="me-2" /> : null}Record salary
              </Button>
            </div>
          </div>
          <Table bordered size="sm" className="align-middle mb-0">
            <thead>
              <tr>
                <th className="text-end">Monthly</th>
                <th>From</th>
                <th>To</th>
                <th style={{ width: 100 }}></th>
              </tr>
            </thead>
            <tbody>
              {history.length ? history.map((row, index) => (
                <tr key={row.id}>
                  <td className="text-end fw-semibold">{money(row.monthlyAmount)}</td>
                  <td className="small">{dayLabel(row.effectiveFrom)}</td>
                  <td className="small">
                    {row.effectiveTo ? dayLabel(row.effectiveTo) : <Badge bg="success">Current</Badge>}
                  </td>
                  <td>
                    {index === 0 && (
                      <Button size="sm" variant="outline-danger" onClick={() => removeRate(row)}>Remove</Button>
                    )}
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={4} className="text-center text-muted py-3">No salary recorded yet.</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" variant="outline-secondary" onClick={() => setSalaryFor(null)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
}
